// VOY-485 (rebuild) — Identity card (Public profile tab).
//
// Avatar tile (initials on v6 gradient), name, Active/Pro pills, joined,
// inline-editable name (single "Full name" field) + bio. Email is read-only (auth-owned).
//
// Wires through `updateProfile()` (the live profile's write path —
// preserved). One inline edit session at a time; clicking another field
// cancels the in-flight one.
//
// Plan pill: any active subscription (active/trialing/past_due) → "Pro member" (purple).
// Otherwise → "Free" (neutral). Detailed tier breakdown lives in the
// PlanWallet section; this is just a presence signal.

import { useState, type ChangeEvent, type KeyboardEvent } from 'react'
import { updateProfile } from '@/lib/profile'
import { isActiveSubscription } from '@/lib/stripe'
import type { Profile } from '@/types/profile'
import type { UserSubscription } from '@/types/subscription'

export interface IdentityCardProps {
  profile: Profile
  subscription: UserSubscription | null
  /** Forwarded after a save so the parent reloads `profile`. */
  onProfileChanged?: () => void
}

type EditableField = 'name' | 'bio'

function joinedDateLabel(iso: string | null | undefined): string {
  if (!iso) return ''
  try {
    return `Joined ${new Date(iso).toLocaleString(undefined, { month: 'short', year: 'numeric' })}`
  } catch {
    return ''
  }
}

function initialsFor(profile: Profile): string {
  // VOY-530: name is now a single "Full name" field, so derive the second initial from
  // the last word of `name` (falling back to legacy `last_name` for un-migrated profiles).
  const nameParts = profile.name?.trim().split(/\s+/).filter(Boolean) ?? []
  const first = nameParts[0]?.[0]
  const last = nameParts.length > 1 ? nameParts[nameParts.length - 1][0] : profile.last_name?.trim()?.[0]
  if (first && last) return (first + last).toUpperCase()
  if (first) return first.toUpperCase()
  const fromUsername = profile.username?.trim()?.[0]
  if (fromUsername) return fromUsername.toUpperCase()
  const fromEmail = profile.email?.trim()?.[0]
  if (fromEmail) return fromEmail.toUpperCase()
  return '?'
}

function fullName(profile: Profile): string {
  const parts = [profile.name, profile.last_name].filter((s): s is string => Boolean(s && s.trim()))
  if (parts.length) return parts.join(' ')
  return profile.username || profile.email || 'Traveler'
}

function isProMember(sub: UserSubscription | null): boolean {
  // VOY-527: delegate to the shared helper — past_due is a grace period, not "Free".
  return isActiveSubscription(sub)
}

export function IdentityCard({ profile, subscription, onProfileChanged }: IdentityCardProps) {
  const [editing, setEditing] = useState<EditableField | null>(null)
  const [draft, setDraft] = useState<string>('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  function beginEdit(field: EditableField, current: string | null | undefined) {
    setEditing(field)
    setDraft(current ?? '')
    setError(null)
  }

  async function commitEdit() {
    if (!editing) return
    setSaving(true)
    setError(null)
    try {
      const trimmed = draft.trim()
      const value = trimmed === '' ? null : trimmed
      // VOY-530: name is edited as a single "Full name" stored in `name`; clear the
      // legacy `last_name` so the combined display never duplicates it.
      const patch: Partial<Profile> =
        editing === 'name' ? { name: value, last_name: null } : { [editing]: value }
      await updateProfile(profile.id, patch)
      setEditing(null)
      onProfileChanged?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed')
    } finally {
      setSaving(false)
    }
  }

  function onDraftChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    setDraft(e.target.value)
  }

  function onKey(e: KeyboardEvent) {
    if (e.key === 'Escape') {
      setEditing(null)
      setError(null)
    }
    if (e.key === 'Enter' && editing !== 'bio') {
      e.preventDefault()
      void commitEdit()
    }
  }

  const subLine = [profile.email, joinedDateLabel(profile.created_at)].filter(Boolean).join(' · ')
  // The name actually on file (combined; empty when neither field is set) — used to prefill the edit.
  const nameValue = [profile.name, profile.last_name]
    .filter((s): s is string => Boolean(s && s.trim()))
    .join(' ')

  return (
    <section className="card" aria-labelledby="identity-h">
      <h3 id="identity-h" className="sr-only">Identity</h3>
      <div style={{ display: 'flex', gap: 18, alignItems: 'flex-start' }}>
        <div className="profile-avatar" aria-hidden="true">{initialsFor(profile)}</div>

        <div style={{ flex: 1 }}>
          <div className="row" style={{ gap: 8, marginBottom: 6 }}>
            <span className="pill mint">● Active</span>
            <span className={isProMember(subscription) ? 'pill purple' : 'pill'}>
              {isProMember(subscription) ? 'Pro member' : 'Free'}
            </span>
          </div>

          {editing === 'name' ? (
            <input
              autoFocus
              aria-label="Full name"
              placeholder="Your name"
              value={draft}
              onChange={onDraftChange}
              onBlur={() => void commitEdit()}
              onKeyDown={onKey}
              disabled={saving}
              style={{ fontSize: 24, fontWeight: 600, padding: '4px 8px', border: '1px solid var(--v6-hair-strong)', borderRadius: 8, background: 'var(--v6-surface-well)', color: 'var(--v6-fg-strong)', marginBottom: 4 }}
            />
          ) : (
            // VOY-530: single editable "Full name" — shows the name on file, or the fallback
            // identity (username/email) when none is set. Click to edit.
            <h2 style={{ fontSize: 24, margin: 0, marginBottom: 2 }}>
              <button
                type="button"
                className="profile-edit-row"
                title="Click to edit your name"
                onClick={() => beginEdit('name', nameValue)}
              >
                {fullName(profile)}
              </button>
            </h2>
          )}

          {subLine && (
            <p style={{ color: 'var(--v6-fg-muted)', margin: 0, fontSize: 13.5 }}>{subLine}</p>
          )}

          {editing === 'bio' ? (
            <textarea
              autoFocus
              aria-label="Bio"
              value={draft}
              onChange={onDraftChange}
              onBlur={() => void commitEdit()}
              onKeyDown={onKey}
              disabled={saving}
              rows={3}
              maxLength={280}
              style={{ display: 'block', width: '100%', marginTop: 12, padding: 10, fontSize: 13.5, lineHeight: 1.55, border: '1px solid var(--v6-hair-strong)', borderRadius: 10, background: 'var(--v6-surface-well)', color: 'var(--v6-fg-body)', fontFamily: 'inherit', resize: 'vertical' }}
            />
          ) : (
            <button
              type="button"
              className="profile-edit-row"
              title="Click to edit bio"
              onClick={() => beginEdit('bio', profile.bio)}
              style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.55, color: profile.bio ? 'var(--v6-fg-body)' : 'var(--v6-fg-muted)' }}
            >
              {profile.bio || 'Add a short bio — a line about how you travel.'}
            </button>
          )}

          {error && (
            <p style={{ color: 'var(--v6-neg)', fontSize: 12, marginTop: 8 }} role="alert">{error}</p>
          )}
        </div>
      </div>
    </section>
  )
}
